import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  ChevronUp,
  Download,
  History,
  House,
  MessageSquare,
  Minus,
  PanelLeftOpen,
  PanelRightOpen,
  Plus,
  RefreshCw,
  ScrollText,
  Search,
  Settings,
  Share2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, ContextMenu, DarkPageIcon, Leaf, Spinner, Splitter, ThemeSwitch, type MenuItem } from "../components/ui";
import { openFind } from "../editor/find";
import { parseOutline, type OutlineEntry } from "../editor/latex";
import { recheckReferences } from "../editor/refs";
import { revealItem, reviewItems, setFocusedItem, type ReviewItem } from "../editor/review";
import { api, errorMessage, type CompileJob, type CompileResult, type LogEntry, type Member, type Project, type ProjectDetails, type ProjectIndex, type TreeEntry } from "../lib/api";
import { Link, navigate, timeAgo, toast, useLocalStorage, useSession } from "../lib/app";
import { CompileButton, jobsFor } from "./CompileButton";
import { receiveEmote } from "./emotes";
import { ConnectDialog } from "./ConnectDialog";
import { SettingsDialog, ShareDialog, SyncDialog, WordCountDialog } from "./Dialogs";
import { Editor, type EditorApi } from "./Editor";
import { EditorTabs } from "./EditorTabs";
import { EditorToolbar } from "./EditorToolbar";
import { FileTree } from "./FileTree";
import { HistoryView } from "./HistoryView";
import { MenuBar, type Layout, type TreeRequest } from "./MenuBar";
import { LogPanel, Outline } from "./Panels";
import { ReviewPanel, reviewCounts, useReviewItems, useReviewSeen, useReviewSummary, withLiveFile, type DraftComment } from "./ReviewPanel";
import { PdfViewer, type PdfViewerApi } from "./PdfViewer";
import { receiveRave } from "./rave";
import { FIT_SCALES, PdfZoomMenu } from "./PdfZoomMenu";
import { SearchPanel } from "./SearchPanel";
import { useProjectView } from "./view";
import { ProjectSession, type PresenceState } from "./session";
import { WhatsNew } from "./WhatsNew";
import { WordProgressDialog } from "./WordCount";

const IMAGE = /\.(png|jpe?g|gif|svg|webp|bmp)$/i;
/** How close to the window's side the editor/PDF divider must be dragged to hide that pane. */
const PANE_EDGE = 80;
const noEditor: EditorApi = { view: null, docId: null, goTo: () => {}, cursorLine: () => null };

export function ProjectPage({ projectId }: { projectId: string }) {
  const { user, canManageSync, updateSettings } = useSession();
  const settings = user!.settings;
  const [details, setDetails] = useState<ProjectDetails | null>(null);
  const [loadError, setLoadError] = useState("");
  const [session, setSession] = useState<ProjectSession | null>(null);
  const [connection, setConnection] = useState("connecting");
  const [presence, setPresence] = useState<PresenceState[]>([]);

  const view = useProjectView(projectId);
  const { openPath: activePath, setOpenPath: setActivePath, tabs, setTabs, closeTabs, restore: restoreView } = view;
  const [mode, setMode] = useState<"editor" | "history">("editor");
  const [mobile, setMobile] = useState<"editor" | "pdf">("editor");
  const [showSidebar, setShowSidebar] = useState(false);
  const [sidebarW, setSidebarW] = useLocalStorage("benleaf:sidebarW", 230);
  const [pdfW, setPdfW] = useLocalStorage("benleaf:pdfW", Math.round(window.innerWidth * 0.42));
  const [outlineH, setOutlineH] = useLocalStorage("benleaf:outlineH", 220);
  const [logOpen, setLogOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useLocalStorage(`benleaf:review:${projectId}`, false);
  const [trackMine, setTrackMine] = useLocalStorage(`benleaf:track:${projectId}`, false);
  const [dialog, setDialog] = useState<null | "sync" | "share" | "connect" | "settings" | "wordcount" | "words">(null);
  const [sidebarMode, setSidebarMode] = useState<"files" | "search">("files");
  const [searchSeed, setSearchSeed] = useState({ text: "", n: 0 });
  const [pdfFind, setPdfFind] = useState(false);
  const [spellMenu, setSpellMenu] = useState<{ x: number; y: number; items: Array<MenuItem | "-"> } | null>(null);

  const [compile, setCompile] = useState<CompileResult | null>(null);
  // The project's compiles, as the server last told us, and this tab's compile requests still waiting for an answer.
  const [compileJobs, setCompileJobs] = useState<CompileJob[]>([]);
  const [requesting, setRequesting] = useState(0);
  const [editedAt, setEditedAt] = useState<number | null>(null);
  const [rawLog, setRawLog] = useState<string | null>(null);
  const [pdfScale, setPdfScale] = useState(settings.pdfZoom ?? "page-width");
  const [zoomPct, setZoomPct] = useState(100);
  const [page, setPage] = useState({ n: 1, total: 0 });
  const [invertPdf, setInvertPdf] = useLocalStorage("benleaf:invertPdf", false);
  const [layout, setLayout] = useLocalStorage<Layout>("benleaf:layout", "split");
  const [focusMode, setFocusMode] = useState(false);
  const [treeRequest, setTreeRequest] = useState<TreeRequest | null>(null);

  // Dragging the editor/PDF divider blurs the panes. Within PANE_EDGE px of either side, the pane on that
  // side hides straight away (the files go with the editor); dragging back out from the edge brings it back.
  const [resizing, setResizing] = useState(false);
  const editorColRef = useRef<HTMLElement>(null);
  const pdfDrag = useRef<{ left: number; right: number; offset: number; startW: number; layout: Layout } | null>(null);
  const pdfSplitter = {
    onDragStart: (x: number) => {
      const col = editorColRef.current!;
      const body = col.parentElement!.getBoundingClientRect();
      // With the editor hidden, the PDF column starts where the body does.
      const left = layout === "pdf" ? body.left : col.getBoundingClientRect().left;
      // Where on the divider it was grabbed, so it doesn't jump under the pointer.
      const offset = layout === "split" ? body.right - x - pdfW : 0;
      pdfDrag.current = { left, right: body.right, offset, startW: pdfW, layout };
    },
    onDrag: (_: number, x: number) => {
      const drag = pdfDrag.current;
      if (!drag) return;
      setResizing(true);
      const next: Layout = x > drag.right - PANE_EDGE ? "editor" : x < drag.left + PANE_EDGE ? "pdf" : "split";
      if (next !== drag.layout) setLayout((drag.layout = next));
      if (next === "split") setPdfW(Math.max(260, Math.min(window.innerWidth - 500, drag.right - x - drag.offset)));
    },
    onDragEnd: () => {
      const drag = pdfDrag.current;
      pdfDrag.current = null;
      setResizing(false);
      // A hidden pane comes back at the width it had before this drag, not where the drag pinned it.
      if (drag && drag.layout !== "split") setPdfW(drag.startW);
    },
  };

  const [index, setIndex] = useState<ProjectIndex | null>(null);
  const indexRef = useRef<ProjectIndex | null>(null);
  indexRef.current = index;
  const [outline, setOutline] = useState<OutlineEntry[]>([]);
  const [cursorLine, setCursorLine] = useState(1);
  const [focusedReview, setFocusedReview] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftComment | null>(null);
  const [pendingJump, setPendingJump] = useState<{ line: number; column?: number; length?: number } | null>(null);
  const [, setTick] = useState(0);

  const editorApi = useRef<EditorApi>(noEditor);
  const pdfApi = useRef<PdfViewerApi>(null);
  const autoTimer = useRef<number | undefined>(undefined);
  const outlineTimer = useRef<number | undefined>(undefined);

  const project = details?.project;
  const role = details?.role ?? "viewer";
  const canEdit = role !== "viewer";
  const tracking = canEdit && (trackMine || !!project?.trackAll);
  const autoCompile = !!settings.autoCompile;
  const compileDraft = !!settings.compileDraft;
  const compileOnly = !!settings.compileOnlyChapter;
  // Full compiles are shared only among people compiling in full mode.
  const myJobs = useMemo(() => jobsFor(compileJobs, !compileDraft && !compileOnly), [compileJobs, compileDraft, compileOnly]);
  const compiling = requesting > 0 || myJobs.length > 0;
  // Read by the compile request and the websocket handler, which outlive renders.
  const modeRef = useRef({ draft: compileDraft, only: compileOnly, path: activePath });
  modeRef.current = { draft: compileDraft, only: compileOnly, path: activePath };
  const activeEntry = details?.tree.find((e) => e.path === activePath && e.type === "file") ?? null;

  // ---------------------------------------------------------------- loading

  const loadDetails = useCallback(async () => {
    try {
      const d = await api.get<ProjectDetails>(`/api/projects/${projectId}`);
      // Same render as the details, so the main-document default below sees the restored file.
      restoreView(d.view);
      setDetails(d);
      setCompileJobs(d.compileJobs ?? []);
      setEditedAt(d.editedAt ?? null);
      if (d.compile) setCompile((c) => (c?.buildId === d.compile!.buildId ? c : d.compile));
      return d;
    } catch (e) {
      setLoadError(errorMessage(e));
      return null;
    }
  }, [projectId, restoreView]);

  const loadTree = useCallback(async () => {
    const r = await api.get<{ tree: TreeEntry[] }>(`/api/projects/${projectId}/tree`).catch(() => null);
    if (r) setDetails((d) => (d ? { ...d, tree: r.tree } : d));
  }, [projectId]);

  const loadIndex = useCallback(() => {
    api.get<ProjectIndex>(`/api/projects/${projectId}/index`).then(setIndex).catch(() => {});
  }, [projectId]);

  /** Catch up on compiles that started, finished or were stopped while the socket was down. */
  const loadCompiles = useCallback(async () => {
    const r = await api
      .get<{ result: CompileResult | null; jobs: CompileJob[]; editedAt: number | null }>(`/api/projects/${projectId}/compile`)
      .catch(() => null);
    if (!r) return;
    setCompileJobs(r.jobs);
    setEditedAt(r.editedAt);
    const res = r.result;
    if (res && !!res.draft === modeRef.current.draft && !!res.onlyRequested === modeRef.current.only) {
      setCompile((c) => (c && c.startedAt >= res.startedAt ? c : res));
    }
  }, [projectId]);

  useEffect(() => {
    loadDetails().then((d) => {
      if (!d) return;
      document.title = `${d.project.name} · benleaf`;
    });
    loadIndex();
    const s = new ProjectSession(projectId, user!);
    setSession(s);
    let wasAway = false;
    const offStatus = s.onStatus((status) => {
      setConnection(status);
      if (status === "away") wasAway = true;
      else if (status === "connected" && wasAway) {
        wasAway = false;
        // Catch up on server events missed while disconnected.
        loadTree();
        loadIndex();
        loadCompiles();
      }
    });
    const offPresence = s.onPresence(() => setPresence(s.presence()));
    const offEvents = s.onEvent((ev) => {
      if (ev.type === "tree") {
        loadTree();
        loadIndex();
      } else if (ev.type === "compile:jobs") setCompileJobs(ev.jobs as CompileJob[]);
      else if (ev.type === "compile:edited") setEditedAt((t) => Math.max(t ?? 0, ev.at as number));
      else if (ev.type === "compile:done") {
        // Everyone's full compiles arrive here, and our own draft and chapter ones; only show builds in our mode.
        const r = ev.result as CompileResult;
        if (!!r.draft === modeRef.current.draft && !!r.onlyRequested === modeRef.current.only) setCompile(r);
        loadIndex();
      } else if (ev.type === "sync" || ev.type === "project") {
        setDetails((d) => (d ? { ...d, project: ev.project as Project } : d));
      } else if (ev.type === "confetti") receiveEmote(ev, user!.id);
      else if (ev.type === "rave") receiveRave(ev, user!.id);
    });
    // Not while away: any request would keep the server awake, or wake it up.
    const idx = setInterval(() => s.status !== "away" && loadIndex(), 45_000);
    const tick = setInterval(() => setTick((t) => t + 1), 30_000);
    // Ask before closing while edits are still on their way. Modern browsers show their own wording instead of ours.
    const beforeUnload = (e: BeforeUnloadEvent) => {
      if (!s.hasUnsynced()) return;
      e.preventDefault();
      e.returnValue = UNSAVED_WARNING;
      // Seen if they choose to stay.
      toast(
        s.status === "connected"
          ? "Still saving your latest edits. Wait until “Saving…” goes away before you close the tab."
          : "You're offline, so your latest edits haven't been saved yet. They're kept on this device and sent when you reconnect.",
        "error",
      );
      return UNSAVED_WARNING;
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => {
      offStatus();
      offPresence();
      offEvents();
      clearInterval(idx);
      clearInterval(tick);
      window.removeEventListener("beforeunload", beforeUnload);
      s.destroy();
      document.title = "benleaf";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Keep tabs in step with the file tree: follow renames, close deleted files, and open the
  // main document the first time the project opens with nothing else open.
  const lastTree = useRef<Map<string, string | undefined> | null>(null);
  useEffect(() => {
    if (!details) return;
    const tree = details.tree;
    const files = new Set(tree.filter((e) => e.type === "file").map((e) => e.path));
    const byDoc = new Map(tree.filter((e) => e.docId).map((e) => [e.docId!, e.path]));
    const before = lastTree.current;
    lastTree.current = new Map(tree.map((e) => [e.path, e.docId]));
    const renames = new Map<string, string>();
    const gone: string[] = [];
    for (const t of new Set([...tabs, ...(activePath ? [activePath] : [])])) {
      if (files.has(t)) continue;
      const moved = before?.get(t) ? byDoc.get(before.get(t)!) : undefined;
      if (moved) renames.set(t, moved);
      // A file that wasn't in the last tree either was just created and the tree hasn't caught up yet.
      else if (!before || before.has(t)) gone.push(t);
    }
    if (renames.size) setTabs([...new Set(tabs.map((t) => renames.get(t) ?? t))], activePath && renames.has(activePath) ? renames.get(activePath) : undefined);
    if (gone.length) closeTabs(gone);
    if (!before && (!activePath || gone.includes(activePath)) && !tabs.some((t) => files.has(t))) {
      const root = tree.find((e) => e.path === details.project.rootDoc) ?? tree.find((e) => e.docId);
      if (root) setActivePath(root.path);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details?.tree]);

  // The toolbar re-reads the editor (bold here? can undo?) after every update.
  const editorListeners = useRef(new Set<() => void>());
  const subscribeEditor = useCallback((fn: () => void) => {
    editorListeners.current.add(fn);
    return () => void editorListeners.current.delete(fn);
  }, []);
  const notifyEditor = useCallback(() => editorListeners.current.forEach((fn) => fn()), []);
  const getEditorView = useCallback(() => editorApi.current.view, []);

  useEffect(() => {
    if (compile?.buildId) {
      setRawLog(null);
      api
        .text(`/api/projects/${projectId}/build/${compile.buildId}/output.log`)
        .then(setRawLog)
        .catch(() => setRawLog(""));
      if (compile.counts.errors > 0 && !compile.pdf) setLogOpen(true);
    }
  }, [compile?.buildId, projectId, compile?.counts.errors, compile?.pdf]);

  // ---------------------------------------------------------------- compile

  const doCompile = useCallback(
    async (force = false, override: { draft?: boolean; only?: boolean } = {}) => {
      clearTimeout(autoTimer.current);
      setRequesting((n) => n + 1);
      const mode = { ...modeRef.current, ...override };
      try {
        const r = await api.post<{ result?: CompileResult; error?: string }>(`/api/projects/${projectId}/compile`, {
          force,
          draft: mode.draft,
          only: mode.only ? mode.path : null,
        });
        // A stopped compile leaves the last PDF on screen. Whoever stopped it already knows.
        if (r.result?.status === "cancelled") {
          if (r.result.stoppedBy && r.result.stoppedBy !== user!.displayName) toast(`${r.result.stoppedBy} stopped the compile`);
        } else if (r.result) setCompile(r.result);
        else if (r.error) toast(r.error, "error");
      } catch (e) {
        // A dropped connection isn't fatal: the result also arrives over the websocket.
        if (!(e instanceof TypeError)) toast(errorMessage(e), "error");
      } finally {
        setRequesting((n) => n - 1);
      }
    },
    [projectId, user],
  );

  const stopCompile = useCallback(
    async (job: CompileJob) => {
      // Shown as stopping straight away; the server's list follows.
      setCompileJobs((jobs) => jobs.map((j) => (j.id === job.id ? { ...j, stoppedBy: user!.displayName } : j)));
      try {
        await api.post(`/api/projects/${projectId}/compile/cancel`, { job: job.id });
      } catch (e) {
        toast(errorMessage(e), "error");
        loadCompiles();
      }
    },
    [projectId, user, loadCompiles],
  );
  const doCompileRef = useRef(doCompile);
  doCompileRef.current = doCompile;

  const onDocChanged = useCallback(() => {
    clearTimeout(outlineTimer.current);
    outlineTimer.current = window.setTimeout(() => {
      const view = editorApi.current.view;
      if (view) setOutline(parseOutline(view.state.doc.toString()));
    }, 400);
    if (!autoCompile || !canEdit) return;
    clearTimeout(autoTimer.current);
    autoTimer.current = window.setTimeout(() => doCompileRef.current(), 2500);
  }, [autoCompile, canEdit]);

  const pdfUrl = compile
    ? compile.pdf
      ? `/api/projects/${projectId}/build/${compile.buildId}/output.pdf`
      : `/api/projects/${projectId}/build/${compile.buildId}/previous.pdf`
    : null;
  const [pdfExists, setPdfExists] = useState(true);
  useEffect(() => {
    if (!compile) return;
    if (compile.pdf) setPdfExists(true);
    else fetch(pdfUrl!, { method: "HEAD" }).then((r) => setPdfExists(r.ok)).catch(() => setPdfExists(false));
  }, [compile, pdfUrl]);

  // ---------------------------------------------------------------- navigation

  const openPath = useCallback(
    (path: string, jump?: { line: number; column?: number; length?: number }) => {
      const entry = details?.tree.find((e) => e.path === path || e.path === path.replace(/^\.\//, ""));
      if (!entry) {
        toast(`${path} isn't in this project`, "error");
        return;
      }
      if (mode === "history") setMode("editor");
      setMobile("editor");
      if (entry.path === activePath && jump) {
        editorApi.current.goTo(jump.line, jump.column, jump.length);
        return;
      }
      setActivePath(entry.path);
      if (jump) setPendingJump(jump);
    },
    [details?.tree, activePath, setActivePath, mode],
  );

  const jumpToLog = (e: LogEntry) => {
    if (!e.file) return;
    let file = e.file;
    if (!details?.tree.some((t) => t.path === file)) {
      const guess = details?.tree.find((t) => t.path.endsWith("/" + file) || t.path === `${file}.tex`);
      if (guess) file = guess.path;
    }
    openPath(file, { line: e.line ?? 1 });
  };

  async function syncToPdf() {
    const pos = editorApi.current.cursorLine();
    if (!compile?.pdf || !pos || !activeEntry) return;
    try {
      const r = await api.get<{ positions: Array<{ page: number; h: number; v: number; width: number; height: number }> }>(
        `/api/projects/${projectId}/synctex/pdf?build=${compile.buildId}&file=${encodeURIComponent(activeEntry.path)}&line=${pos.line}&column=${pos.column}`,
      );
      if (r.positions.length) {
        setMobile("pdf");
        setLayout((l) => (l === "editor" ? "split" : l));
        pdfApi.current?.highlight(r.positions);
      } else toast("Couldn't find that line in the PDF. Try recompiling.");
    } catch (e) {
      toast(errorMessage(e), "error");
    }
  }

  async function syncToCode(pageN: number, h: number, v: number) {
    if (!compile?.pdf) return;
    try {
      const r = await api.get<{ location: { file: string; line: number; column: number } | null }>(
        `/api/projects/${projectId}/synctex/code?build=${compile.buildId}&page=${pageN}&h=${h}&v=${v}`,
      );
      if (r.location) {
        setLayout((l) => (l === "pdf" ? "split" : l));
        openPath(r.location.file, { line: r.location.line });
      }
    } catch (e) {
      toast(errorMessage(e), "error");
    }
  }

  // Labels and bibliography keys elsewhere in the project may have changed.
  useEffect(() => {
    const view = editorApi.current.view;
    if (view) recheckReferences(view);
  }, [index]);

  /** Search every file for `text`, in the sidebar. */
  const searchProject = useCallback(
    (text: string) => {
      setSearchSeed((s) => ({ text: text.length <= 200 && !text.includes("\n") ? text : "", n: s.n + 1 }));
      setSidebarMode("search");
      setShowSidebar(true);
      setMode("editor");
      // The sidebar is hidden in focus mode and PDF-only view.
      setFocusMode(false);
      setLayout((l) => (l === "pdf" ? "split" : l));
    },
    [setLayout],
  );
  const openSearch = useCallback(() => {
    const view = editorApi.current.view;
    const sel = view?.state.selection.main;
    searchProject(view && sel && !sel.empty ? view.state.sliceDoc(sel.from, sel.to) : "");
  }, [searchProject]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && !e.altKey && e.key.toLowerCase() === "f") {
        e.preventDefault();
        openSearch();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openSearch]);
  // Ctrl/⌘+F anywhere on the page opens the editor's find bar rather than the browser's find.
  // The editor, its find bar and the PDF column handle it themselves first.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey || e.key.toLowerCase() !== "f" || e.defaultPrevented) return;
      if (mode !== "editor" || document.querySelector(".modal")) return;
      const view = editorApi.current.view;
      const pdfOnly = window.innerWidth <= 900 && mobile === "pdf";
      if (view && !pdfOnly) {
        e.preventDefault();
        openFind(view);
      } else if (pdfUrl && pdfExists) {
        e.preventDefault();
        setPdfFind(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, mobile, pdfUrl, pdfExists]);

  // ---------------------------------------------------------------- presence

  const presenceByDoc = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const p of presence) {
      if (!p.docId || p.user.id === user!.id) continue;
      m.set(p.docId, [...(m.get(p.docId) ?? []), p.user.color]);
    }
    return m;
  }, [presence, user]);
  const others = useMemo(() => {
    const seen = new Map<number, PresenceState>();
    for (const p of presence) if (p.user.id !== user!.id) seen.set(p.user.id, p);
    return [...seen.values()];
  }, [presence, user]);

  /** Open the file someone is in and move to their cursor. */
  const goToPerson = async (p: PresenceState) => {
    const entry = p.docId ? details?.tree.find((e) => e.docId === p.docId) : undefined;
    if (!entry || !session) {
      toast(`${p.user.name} doesn't have a file open`);
      return;
    }
    openPath(entry.path);
    const at = await session.cursorOf(p);
    // Don't pull you back if you've opened something else while we waited.
    if (modeRef.current.path !== entry.path) return;
    if (at) setPendingJump(at);
    else toast(`${p.user.name} is in ${entry.path} but hasn't placed their cursor yet`);
  };

  // ---------------------------------------------------------------- review

  const reviewHandle = activeEntry?.docId ? (session?.getDoc(activeEntry.docId) ?? null) : null;
  const reviewOpenFile = useMemo(() => (activeEntry?.docId ? { docId: activeEntry.docId, path: activeEntry.path } : null), [activeEntry?.docId, activeEntry?.path]);
  const reviewLive = useReviewItems(reviewHandle);
  const reviewSummary = useReviewSummary(projectId, reviewHandle, connection === "away");
  const reviewSeen = useReviewSeen(projectId, user!.id);
  const reviewSynced = !!reviewHandle?.isSynced;
  const reviewFiles = useMemo(
    () => withLiveFile(reviewSummary.files, reviewOpenFile, reviewSynced ? reviewLive : null),
    [reviewSummary.files, reviewOpenFile, reviewSynced, reviewLive],
  );
  const reviewCount = useMemo(() => reviewCounts(reviewFiles ?? [], reviewSeen), [reviewFiles, reviewSeen]);
  const [reviewNav, setReviewNav] = useState<{ dir: 1 | -1; n: number } | null>(null);
  /** A comment or change to select once its file has opened. */
  const pendingReveal = useRef<{ docId: string; id: string } | null>(null);

  const toggleReview = (open: boolean) => {
    setReviewOpen(open);
    setReviewNav(null);
  };
  const focusReview = (file: { docId: string; path: string }, item: ReviewItem) => {
    setFocusedReview(item.id);
    const view = editorApi.current.view;
    if (file.docId === activeEntry?.docId) {
      if (view) revealItem(view, item);
      return;
    }
    pendingReveal.current = { docId: file.docId, id: item.id };
    openPath(file.path);
  };
  const revealPending = (view: Parameters<typeof revealItem>[0]) => {
    const want = pendingReveal.current;
    if (!want || want.docId !== activeEntry?.docId) return;
    pendingReveal.current = null;
    const h = session?.getDoc(want.docId);
    const item = h && reviewItems(h.ydoc, h.ytext).find((i) => i.id === want.id);
    if (item) revealItem(view, item);
  };

  // ---------------------------------------------------------------- render

  if (loadError) {
    return (
      <div className="page">
        <div className="empty-state">
          <h3>Can't open this project</h3>
          <p>{loadError}</p>
          <Link to="/">Back to projects</Link>
        </div>
      </div>
    );
  }
  if (!details || !project || !session) {
    return (
      <div className="page" style={{ display: "grid", placeItems: "center" }}>
        <Spinner size={22} />
      </div>
    );
  }

  const syncChip = project.linked ? (
    project.syncState.status === "conflict" || project.syncState.status === "error" ? (
      <span className="chip chip-bad">
        <span className="dot" />
        {project.syncState.status}
      </span>
    ) : project.syncState.behind ? (
      <span className="chip chip-warn">{project.syncState.behind} to pull</span>
    ) : null
  ) : null;
  const syncButtons = (
    <>
      <button className="icon-btn" title="Find in PDF" disabled={!compile?.pdf || !activeEntry?.docId} onClick={syncToPdf}>
        <ArrowRight size={15} />
      </button>
      <button
        className="icon-btn"
        title="Find in code (or double-click the PDF)"
        disabled={!compile?.pdf}
        onClick={() => {
          const p = pdfApi.current?.visiblePoint();
          if (p) syncToCode(p.page, p.h, p.v);
        }}
      >
        <ArrowLeft size={15} />
      </button>
    </>
  );

  const compileButton = (
    <CompileButton
      canEdit={canEdit}
      jobs={myJobs}
      requesting={requesting > 0}
      shown={compile}
      editedAt={editedAt}
      me={user!.displayName}
      autoCompile={autoCompile}
      draft={compileDraft}
      only={compileOnly}
      onCompile={(force) => doCompile(force)}
      onStop={stopCompile}
      onRestart={async (job) => {
        await stopCompile(job);
        doCompile(job.force, { draft: job.draft, only: !!job.only });
      }}
      // Changing the mode doesn't compile: people often switch both, or want to finish an edit first.
      onSettings={(patch) => updateSettings(patch).catch((e) => toast(errorMessage(e), "error"))}
      onClearCache={async () => {
        await api.post(`/api/projects/${projectId}/compile/clear`).catch((e) => toast(errorMessage(e), "error"));
        toast("Cache cleared");
      }}
      onWordCount={() => setDialog("wordcount")}
      onWordProgress={() => setDialog("words")}
      onDownloadSource={() => (location.href = `/api/projects/${projectId}/download.zip`)}
    />
  );

  return (
    <div className={`workspace${focusMode ? " focus-mode" : ""}`}>
      <ReconnectBubble connection={connection} />
      <header className="topbar">
        {mode === "history" ? (
          <button className="home-back" onClick={() => setMode("editor")} title="Back to editor">
            <ArrowLeft size={16} /> <span className="hide-narrow">Back to editor</span>
          </button>
        ) : (
          <Link to="/" className="home" aria-label="All projects">
            <span className="home-icon">
              <Leaf />
              <House size={18} className="home-house" />
            </span>
            <span className="home-tip">All projects</span>
          </Link>
        )}
        <ProjectTitle project={project} canEdit={canEdit} onRenamed={(p) => setDetails({ ...details, project: p })} />
        <MenuBar
          project={project}
          tree={details.tree}
          canEdit={canEdit}
          getView={() => editorApi.current.view}
          pdfUrl={pdfUrl && pdfExists ? pdfUrl : null}
          layout={layout}
          onLayout={setLayout}
          focusMode={focusMode}
          onFocusMode={setFocusMode}
          invertPdf={invertPdf}
          onInvertPdf={setInvertPdf}
          onTreeRequest={(r) => {
            setMode("editor");
            setSidebarMode("files");
            setFocusMode(false);
            setLayout((l) => (l === "pdf" ? "split" : l));
            setTreeRequest(r);
          }}
          onHistory={() => setMode("history")}
          onWordCount={() => setDialog("wordcount")}
          onShare={() => setDialog("share")}
          onConnect={() => setDialog("connect")}
          onSettings={() => setDialog("settings")}
          onFindAll={openSearch}
          onComment={() => {
            const view = editorApi.current.view;
            const s = view?.state.selection.main;
            if (!view || !s || s.empty) return;
            setReviewOpen(true);
            setDraft({ from: s.from, to: s.to, quote: view.state.sliceDoc(s.from, s.to).slice(0, 300) });
          }}
        />
        <div className="mobile-switch">
          <button className="btn btn-sm btn-ghost" onClick={() => setShowSidebar(!showSidebar)}>
            Files
          </button>
          <button className={`btn btn-sm btn-ghost${mobile === "editor" ? " on" : ""}`} onClick={() => setMobile("editor")}>
            Code
          </button>
          <button className={`btn btn-sm btn-ghost${mobile === "pdf" ? " on" : ""}`} onClick={() => setMobile("pdf")}>
            PDF
          </button>
        </div>
        <div className="grow" />
        <div className="presence hide-narrow">
          {others.slice(0, 5).map((p) => (
            <button key={p.user.id} className="presence-btn" onClick={() => goToPerson(p)}>
              <Avatar
                id={p.user.id}
                name={p.user.name}
                title={p.user.claude ? `Claude is editing for ${p.user.for ?? "someone"}. Go to it` : `Go to ${p.user.name}`}
                image={p.user.claude ? "/claude.jpg" : undefined}
                size={26}
                ring
              />
            </button>
          ))}
        </div>
        <WhatsNew />
        <button
          className={`btn btn-ghost btn-sm${reviewOpen ? " on" : ""}`}
          onClick={() => toggleReview(!reviewOpen)}
          title={`Comments & tracked changes${reviewCount.open ? `: ${reviewCount.open} open${reviewCount.unread ? `, ${reviewCount.unread} new` : ""}` : ""}`}
        >
          <MessageSquare size={15} /> <span className="hide-narrow">Review</span>
          {reviewCount.open > 0 && <span className={`count-badge${reviewCount.unread ? " new" : ""}`}>{reviewCount.unread || reviewCount.open}</span>}
        </button>
        <button className={`btn btn-ghost btn-sm${mode === "history" ? " on" : ""}`} onClick={() => setMode(mode === "history" ? "editor" : "history")} title="History">
          <History size={15} /> <span className="hide-narrow">History</span>
        </button>
        <button className="btn btn-ghost btn-sm" onClick={() => setDialog("sync")} title="Version control & sync">
          <RefreshCw size={15} /> <span className="hide-narrow">{project.linked ? "Sync" : "Commit"}</span> {syncChip}
        </button>
        <button className="btn btn-ghost btn-sm" onClick={() => setDialog("share")} title="Share">
          <Share2 size={15} /> <span className="hide-narrow">Share</span>
        </button>
        <ThemeSwitch className="hide-narrow" />
        <button className="icon-btn" onClick={() => setDialog("settings")} title="Settings">
          <Settings size={16} />
        </button>
        {/* Recompile lives in the PDF toolbar; it's up here only while the PDF column is hidden. */}
        {layout === "editor" ? (
          <>
            <span className="sep" />
            {compileButton}
          </>
        ) : (
          <span className="only-narrow">
            <span className="sep" />
            {compileButton}
          </span>
        )}
      </header>

      <div className={`ws-body layout-${layout}${showSidebar ? " show-sidebar" : ""}${resizing ? " resizing" : ""}`} data-mobile={window.innerWidth <= 900 ? mobile : undefined}>
        {mode === "history" ? (
          <HistoryView projectId={projectId} canEdit={canEdit} />
        ) : (
          <>
            <aside className="sidebar" style={{ width: sidebarW }}>
              {sidebarMode === "search" ? (
                <SearchPanel
                  projectId={projectId}
                  session={session}
                  canEdit={canEdit}
                  trackAs={tracking ? { id: user!.id, name: user!.displayName } : null}
                  seed={searchSeed}
                  onOpen={(path, jump) => {
                    setShowSidebar(false);
                    openPath(path, jump);
                  }}
                  onClose={() => setSidebarMode("files")}
                />
              ) : (
              <>
              <FileTree
                projectId={projectId}
                entries={details.tree}
                activePath={activePath}
                expanded={view.expanded}
                onExpandedChange={view.setExpanded}
                rootDoc={project.rootDoc}
                canEdit={canEdit}
                presence={presenceByDoc}
                onOpen={(e) => {
                  setActivePath(e.path);
                  setShowSidebar(false);
                  setMobile("editor");
                }}
                onSetRoot={async (path) => {
                  const r = await api.patch<{ project: Project }>(`/api/projects/${projectId}`, { rootDoc: path }).catch((e) => {
                    toast(errorMessage(e), "error");
                    return null;
                  });
                  if (r) setDetails({ ...details, project: r.project });
                }}
                onChanged={() => {
                  loadTree();
                  loadIndex();
                }}
                onSearch={openSearch}
                request={treeRequest}
                onRequestDone={() => setTreeRequest(null)}
              />
              <Splitter vertical={false} onDrag={(d) => setOutlineH((h) => Math.max(60, Math.min(window.innerHeight - 200, h - d)))} />
              <div className="panel" style={{ height: outlineH }}>
                <div className="panel-head">
                  <span className="title">Outline</span>
                  {compile?.words && (
                    <button
                      className="wc-link"
                      onClick={() => setDialog("words")}
                      title={project.wordTarget ? `Target: ${project.wordTarget.toLocaleString()} words` : "Word progress"}
                    >
                      {compile.words.total.toLocaleString()}
                      {project.wordTarget ? ` / ${project.wordTarget.toLocaleString()}` : ""} words
                    </button>
                  )}
                </div>
                <div className="panel-body">
                  <Outline entries={outline} currentLine={cursorLine} onJump={(line) => editorApi.current.goTo(line)} />
                </div>
              </div>
              </>
              )}
            </aside>
            <Splitter onDrag={(d) => setSidebarW((w) => Math.max(160, Math.min(480, w + d)))} />

            <section className="editor-col" ref={editorColRef}>
              <EditorTabs
                tabs={tabs}
                active={activePath}
                tree={details.tree}
                presence={presenceByDoc}
                onSelect={(path) => openPath(path)}
                onClose={closeTabs}
                onReorder={(next) => setTabs(next)}
              >
                <div className="etabs-end">
                  {!tabs.length && <span className="etabs-none">No file open</span>}
                  <SaveStatus session={session} connection={connection} />
                </div>
              </EditorTabs>
              {activeEntry?.docId && (
                <EditorToolbar
                  getView={getEditorView}
                  subscribe={subscribeEditor}
                  canEdit={canEdit}
                  tracking={tracking}
                  trackAll={!!project.trackAll}
                  onTracking={(on) => !project.trackAll && setTrackMine(on)}
                  mode={settings.editorMode ?? "code"}
                  visualAvailable={/\.tex$/i.test(activeEntry.path)}
                  onMode={(editorMode) => updateSettings({ editorMode }).catch((e) => toast(errorMessage(e), "error"))}
                  onComment={(from, to) => {
                    setReviewOpen(true);
                    const view = editorApi.current.view;
                    setDraft({ from, to, quote: view ? view.state.sliceDoc(from, to).slice(0, 300) : "" });
                  }}
                  figureFiles={() => indexRef.current?.files ?? details.tree.filter((e) => e.type === "file").map((e) => e.path)}
                />
              )}
              <div className="editor-main">
                {activeEntry?.docId ? (
                  <Editor
                    key={activeEntry.docId}
                    session={session}
                    docId={activeEntry.docId}
                    path={activeEntry.path}
                    user={user!}
                    role={role}
                    settings={settings}
                    tracking={tracking}
                    reviewOpen={reviewOpen}
                    getIndex={() => indexRef.current}
                    onCompile={() => doCompileRef.current()}
                    apiRef={editorApi}
                    onCursorLine={setCursorLine}
                    onDocChanged={onDocChanged}
                    onViewUpdate={notifyEditor}
                    onReady={(view) => {
                      setOutline(parseOutline(view.state.doc.toString()));
                      revealPending(view);
                      notifyEditor();
                    }}
                    pendingJump={pendingJump}
                    onJumpDone={() => setPendingJump(null)}
                    savedPosition={() => view.positionOf(activeEntry.docId!)}
                    onPosition={(at) => view.setPosition(activeEntry.docId!, at)}
                    onOpenLocation={(file, line) => openPath(file, { line })}
                    onSearchAll={searchProject}
                    onFocusReviewItem={(id) => {
                      setReviewOpen(true);
                      setFocusedReview(id);
                      editorApi.current.view?.dispatch({ effects: setFocusedItem.of(id) });
                    }}
                    onRequestComment={(from, to) => {
                      setReviewOpen(true);
                      const view = editorApi.current.view;
                      setDraft({ from, to, quote: view ? view.state.sliceDoc(from, to).slice(0, 300) : "" });
                    }}
                    onReviewNav={(dir) => {
                      setReviewOpen(true);
                      setReviewNav((r) => ({ dir, n: (r?.n ?? 0) + 1 }));
                    }}
                    onSpellMenu={({ x, y, word, suggestions, regional, replace, learn }) => {
                      const options = [
                        ...(regional ? [{ label: regional.spelling, hint: regional.label, onSelect: () => replace(regional.spelling) }] : []),
                        ...suggestions.filter((s) => s !== regional?.spelling).map((s) => ({ label: s, onSelect: () => replace(s) })),
                      ];
                      setSpellMenu({
                        x,
                        y,
                        items: [
                          ...(options.length ? options : [{ label: "No suggestions", disabled: true, onSelect: () => {} }]),
                          "-" as const,
                          {
                            label: `Add “${word}” to dictionary`,
                            onSelect: () => {
                              learn();
                              updateSettings({ dictionary: [...new Set([...(settings.dictionary ?? []), word])] });
                            },
                          },
                        ],
                      });
                    }}
                  />
                ) : activeEntry ? (
                  <BinaryPreview projectId={projectId} entry={activeEntry} />
                ) : (
                  <div className="editor-empty">
                    <div>
                      <div className="big">Pick a file</div>
                      Choose a file on the left to start editing.
                    </div>
                  </div>
                )}
                {reviewOpen && (
                  <ReviewPanel
                    session={session}
                    handle={reviewHandle}
                    open={reviewOpenFile}
                    items={reviewLive}
                    files={reviewFiles}
                    user={user!}
                    canEdit={canEdit}
                    tracking={tracking}
                    focusedId={focusedReview}
                    draft={draft}
                    seen={reviewSeen}
                    navRequest={reviewNav}
                    cursor={() => editorApi.current.view?.state.selection.main.head ?? null}
                    onDraftDone={() => setDraft(null)}
                    onFocus={focusReview}
                    onFileItems={reviewSummary.setFileItems}
                    onClose={() => toggleReview(false)}
                  />
                )}
              </div>
            </section>
            {/* With a pane hidden, the divider sits at that edge so it can be dragged back out. */}
            <Splitter {...pdfSplitter} className={layout === "editor" ? "splitter-edge-right" : layout === "pdf" ? "splitter-edge-left" : undefined}>
              {layout === "split" ? (
                syncButtons
              ) : (
                <button className="icon-btn" title={layout === "editor" ? "Show the PDF" : "Show the editor"} onClick={() => setLayout("split")}>
                  {layout === "editor" ? <PanelRightOpen size={15} /> : <PanelLeftOpen size={15} />}
                </button>
              )}
            </Splitter>

            <section
              className="pdf-col"
              style={{ width: pdfW }}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === "f") {
                  e.preventDefault();
                  setPdfFind(true);
                }
              }}
            >
              <div className="pdf-toolbar">
                {/* The top bar has its own Recompile on narrow screens, where this toolbar has no room. */}
                <span className="hide-narrow">{compileButton}</span>
                {/* Hidden while compiling (the Recompile button shows progress), but it keeps its space so Download doesn't jump. */}
                <div className={`compile-status${compiling ? " busy" : ""}`}>
                  {compile ? (
                    <>
                      {compile.status === "success" ? (
                        <span title={`${compile.compiler}, ${(compile.durationMs / 1000).toFixed(1)}s`}>
                          Compiled {timeAgo(compile.startedAt)}
                          {compile.by && compile.by !== user!.displayName ? ` by ${compile.by}` : ""}
                        </span>
                      ) : (
                        <span className="error-text">{compile.status === "timeout" ? "Timed out" : compile.status === "error" ? "Couldn't compile" : "No PDF produced"}</span>
                      )}
                      {compile.draft && (
                        <span className="chip" title="Images are shown as boxes. Turn draft mode off in the Recompile menu for the final PDF.">
                          draft
                        </span>
                      )}
                      {compile.onlyRequested && (
                        <span className={`chip${compile.only ? "" : " chip-warn"}`} title={compile.note}>
                          {compile.only ? `${compile.only.split("/").pop()} only` : "whole document"}
                        </span>
                      )}
                    </>
                  ) : (
                    "Not compiled yet"
                  )}
                </div>
                {/* Whichever PDF is on screen, including the last good one after a failed compile.
                    `download` keeps the editor open: no "leave site?" prompt while saving, and an error doesn't replace the page. */}
                <a
                  className={`icon-btn${pdfUrl && pdfExists ? "" : " disabled"}`}
                  title={compile && !compile.pdf ? "Download PDF (last successful compile)" : "Download PDF"}
                  href={pdfUrl && pdfExists ? `${pdfUrl}?download=1` : undefined}
                  aria-disabled={!(pdfUrl && pdfExists)}
                  download
                >
                  <Download size={15} />
                </a>
                <span className="grow" />
                {/* The divider is hidden on narrow screens, so the sync buttons live here instead. */}
                <span className="only-narrow">{syncButtons}</span>
                <span className="sep hide-narrow" />
                <button className="icon-btn" title="Zoom out" onClick={() => pdfApi.current?.zoomOut()}>
                  <Minus size={15} />
                </button>
                <button className="icon-btn" title="Zoom in" onClick={() => pdfApi.current?.zoomIn()}>
                  <Plus size={15} />
                </button>
                <PdfZoomMenu
                  percent={zoomPct}
                  canPresent={!!pdfUrl && pdfExists}
                  onZoomIn={() => pdfApi.current?.zoomIn()}
                  onZoomOut={() => pdfApi.current?.zoomOut()}
                  onScale={(value) => {
                    setPdfScale(value);
                    updateSettings({ pdfZoom: value }).catch(() => {});
                  }}
                  onPresent={() => pdfApi.current?.present()}
                />
                <button
                  className="icon-btn page-btn hide-narrow"
                  title="Previous page"
                  disabled={!pdfUrl || !pdfExists || page.n <= 1}
                  onClick={() => pdfApi.current?.previousPage()}
                >
                  <ChevronUp size={15} />
                </button>
                <button
                  className="icon-btn page-btn hide-narrow"
                  title="Next page"
                  disabled={!pdfUrl || !pdfExists || page.n >= page.total}
                  onClick={() => pdfApi.current?.nextPage()}
                >
                  <ChevronDown size={15} />
                </button>
                <span className="zoom hide-narrow">{page.total ? `${page.n}/${page.total}` : ""}</span>
                <button className={`icon-btn${pdfFind ? " on" : ""}`} title="Search the PDF (Ctrl/⌘+F)" disabled={!pdfUrl || !pdfExists} onClick={() => setPdfFind(!pdfFind)}>
                  <Search size={15} />
                </button>
                <button
                  className={`icon-btn${invertPdf ? " on" : ""}`}
                  title={invertPdf ? "Dark PDF is on: click for white pages" : "Dark PDF: light text on dark pages"}
                  aria-label="Dark PDF"
                  aria-pressed={invertPdf}
                  onClick={() => setInvertPdf(!invertPdf)}
                >
                  <DarkPageIcon />
                </button>
                <button className={`btn btn-sm btn-ghost${logOpen ? " on" : ""}`} onClick={() => setLogOpen(!logOpen)} title="Logs and errors">
                  <ScrollText size={14} />
                  {compile && compile.counts.errors > 0 && <span className="badge badge-error">{compile.counts.errors}</span>}
                  {compile && compile.counts.warnings > 0 && <span className="badge badge-warning">{compile.counts.warnings}</span>}
                </button>
              </div>
              <div className="pdf-stage">
                <PdfViewer
                  ref={pdfApi}
                  url={pdfUrl && pdfExists ? pdfUrl : null}
                  scale={pdfScale}
                  inverted={invertPdf}
                  onScaleChange={(value, cur) => {
                    setZoomPct(Math.round(cur * 100));
                    if (!FIT_SCALES.includes(value)) setPdfScale(value);
                  }}
                  onPageChange={(n, total) => setPage({ n, total })}
                  onSyncToCode={syncToCode}
                  savedPosition={view.pdfPosition}
                  onPosition={view.setPdfPosition}
                  findOpen={pdfFind && !!pdfUrl && pdfExists && !logOpen}
                  onCloseFind={() => setPdfFind(false)}
                />
                {(!pdfUrl || !pdfExists) && !logOpen && (
                  <div className="pdf-placeholder">
                    <div>
                      <div className="big">{compiling ? "Typesetting…" : compile ? "No PDF yet" : "Ready when you are"}</div>
                      <div className="small">{compile && !compile.pdf ? "Open the log to see what went wrong." : "Recompile to see the PDF here."}</div>
                    </div>
                  </div>
                )}
                {logOpen && <LogPanel result={compile} rawLog={rawLog} onJump={jumpToLog} onClose={() => setLogOpen(false)} />}
              </div>
            </section>
          </>
        )}
      </div>

      {spellMenu && <ContextMenu x={spellMenu.x} y={spellMenu.y} items={spellMenu.items} onClose={() => setSpellMenu(null)} />}
      {dialog === "sync" && (
        <SyncDialog
          project={project}
          role={role}
          canManageSync={canManageSync}
          onClose={() => setDialog(null)}
          onProject={(p) => setDetails((d) => (d ? { ...d, project: p } : d))}
        />
      )}
      {dialog === "share" && (
        <ShareDialog
          projectId={projectId}
          role={role}
          members={details.members}
          pendingInvites={details.pendingInvites ?? []}
          onSharing={(s) => setDetails({ ...details, members: s.members, pendingInvites: s.pendingInvites })}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "connect" && <ConnectDialog projectId={projectId} onClose={() => setDialog(null)} />}
      {dialog === "wordcount" && <WordCountDialog projectId={projectId} onClose={() => setDialog(null)} />}
      {dialog === "words" && (
        <WordProgressDialog
          project={project}
          words={compile?.words ?? null}
          canEdit={canEdit}
          compiling={compiling}
          onCompile={() => doCompile()}
          onProject={(p) => setDetails((d) => (d ? { ...d, project: p } : d))}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "settings" && (
        <SettingsDialog
          project={project}
          role={role}
          tree={details.tree}
          settings={settings}
          onSettings={(s) => updateSettings(s).catch((e) => toast(errorMessage(e), "error"))}
          onProject={(p) => setDetails({ ...details, project: p })}
          onClose={() => setDialog(null)}
          onClearCache={async () => {
            await api.post(`/api/projects/${projectId}/compile/clear`).catch((e) => toast(errorMessage(e), "error"));
            toast("Compile cache cleared");
          }}
        />
      )}
    </div>
  );
}

const UNSAVED_WARNING = "Your latest edits haven't been saved yet. If you leave now, they're sent the next time you open this project on this device.";

/** Says when edits are slow to reach the server (or can't), so people know not to close the tab yet. */
function SaveStatus({ session, connection }: { session: ProjectSession; connection: string }) {
  const [unsaved, setUnsaved] = useState(false);
  useEffect(() => {
    let timer: number | undefined;
    const update = () => {
      clearTimeout(timer);
      // Edits normally land within milliseconds, so only speak up when it's taking a while.
      if (session.hasUnsynced()) timer = window.setTimeout(() => setUnsaved(session.hasUnsynced()), 1000);
      else setUnsaved(false);
    };
    update();
    const off = session.onPendingChange(update);
    return () => {
      off();
      clearTimeout(timer);
    };
  }, [session]);
  if (connection === "away") {
    return (
      <span className="chip" title="Disconnected while nobody was using this tab. Move the mouse or press a key to reconnect.">
        paused
        {unsaved ? " · edits kept locally" : ""}
      </span>
    );
  }
  if (connection !== "connected") {
    return (
      <span className="chip chip-warn" title={unsaved ? "Your latest edits are kept on this device and sent when you reconnect" : undefined}>
        {connection === "connecting" ? "connecting…" : "offline"}
        {unsaved ? " · edits kept locally" : ""}
      </span>
    );
  }
  return unsaved ? (
    <span className="chip" title="Your latest edits are on their way to the server">
      Saving…
    </span>
  ) : null;
}

/** When someone comes back after an idle disconnect, shows the page reconnecting, then briefly that it's done. */
function ReconnectBubble({ connection }: { connection: string }) {
  const [shown, setShown] = useState<"connecting" | "connected" | null>(null);
  const wasAway = useRef(false);
  const since = useRef(0);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    if (connection === "away") {
      wasAway.current = true;
      clearTimeout(timer.current);
      setShown(null);
      return;
    }
    if (!wasAway.current) return;
    if (connection !== "connected") {
      if (!since.current) since.current = Date.now();
      setShown("connecting");
      return;
    }
    wasAway.current = false;
    // Even an instant reconnect shows "Reconnecting…" long enough to read.
    const wait = Math.max(0, since.current + 700 - Date.now());
    since.current = 0;
    clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      setShown("connected");
      timer.current = window.setTimeout(() => setShown(null), 1600);
    }, wait);
  }, [connection]);
  if (!shown) return null;
  return (
    <div className={`reconnect-bubble${shown === "connected" ? " reconnect-done" : ""}`} role="status" aria-live="polite">
      {shown === "connecting" ? <Spinner size={12} /> : <Check size={14} />}
      {shown === "connecting" ? "Reconnecting…" : "Connected"}
    </div>
  );
}

function ProjectTitle({ project, canEdit, onRenamed }: { project: Project; canEdit: boolean; onRenamed: (p: Project) => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(project.name);
  if (!editing) {
    return (
      <button className="project-title" title={canEdit ? "Rename" : project.name} onClick={() => canEdit && (setValue(project.name), setEditing(true))}>
        {project.name}
      </button>
    );
  }
  const save = async () => {
    setEditing(false);
    if (!value.trim() || value === project.name) return;
    try {
      const r = await api.patch<{ project: Project }>(`/api/projects/${project.id}`, { name: value });
      onRenamed(r.project);
    } catch (e) {
      toast(errorMessage(e), "error");
    }
  };
  return (
    <input
      className="project-title"
      autoFocus
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => {
        if (e.key === "Enter") save();
        if (e.key === "Escape") setEditing(false);
      }}
    />
  );
}

function BinaryPreview({ projectId, entry }: { projectId: string; entry: TreeEntry }) {
  const url = `/api/projects/${projectId}/raw?path=${encodeURIComponent(entry.path)}`;
  if (IMAGE.test(entry.path)) {
    return (
      <div className="image-preview">
        <img src={url} alt={entry.path} />
      </div>
    );
  }
  return (
    <div className="editor-empty">
      <div>
        <div className="big">{entry.path.split("/").pop()}</div>
        <p>
          {entry.size !== undefined ? `${(entry.size / 1024).toFixed(1)} KB · ` : ""}
          This file can't be edited here.
        </p>
        <a className="btn" href={`${url}&download=1`}>
          <Download size={14} /> Download
        </a>
      </div>
    </div>
  );
}

export { navigate };
